import type {
  AutomaticRegistrationMetrics,
  AutomaticRegistrationOutcome,
  AutomaticRegistrationPolicy,
  AutomaticRegistrationRejection,
  AutomaticWorkflowBlank,
  AutomaticWorkflowDecision,
  AutomaticWorkflowReason,
} from "./types";

/**
 * A match attempt that has not run, or that produced no homography at all.
 * This is the state of every session until the automatic matcher exists.
 */
export const REGISTRATION_UNAVAILABLE: AutomaticRegistrationOutcome =
  Object.freeze({
    rejections: Object.freeze([]) as readonly AutomaticRegistrationRejection[],
    status: "unavailable",
  });

function inlierRatio(metrics: AutomaticRegistrationMetrics): number {
  if (metrics.matchedCount <= 0) return 0;
  return metrics.inlierCount / metrics.matchedCount;
}

/**
 * Grades a completed trace-to-reference match against a supplied policy. The
 * policy is always passed in; no threshold is assumed here, because the
 * accepted values come from measured local diagnostics, not from this module.
 */
export function evaluateAutomaticRegistration(
  metrics: AutomaticRegistrationMetrics,
  policy: AutomaticRegistrationPolicy,
): AutomaticRegistrationOutcome {
  const rejections: AutomaticRegistrationRejection[] = [];

  if (!metrics.withinReferenceExtent) rejections.push("EXTENT");
  if (metrics.inlierCount < policy.minimumInlierCount)
    rejections.push("INLIER_COUNT");
  if (inlierRatio(metrics) < policy.minimumInlierRatio)
    rejections.push("INLIER_RATIO");
  if (!metrics.orientationPreserved) rejections.push("ORIENTATION");
  if (metrics.reprojectionErrorPx > policy.maximumReprojectionErrorPx)
    rejections.push("REPROJECTION_ERROR");

  return {
    rejections,
    status: rejections.length === 0 ? "accepted" : "rejected",
  };
}

/**
 * Decides what the trainee is shown. The requirement is that a confident
 * automatic match with every required value resolved asks him for nothing:
 * `mode` is `automatic` and `manualGridRegistrationOffered` is false.
 */
export function decideAutomaticWorkflow(
  registration: AutomaticRegistrationOutcome,
  blanks: readonly AutomaticWorkflowBlank[],
): AutomaticWorkflowDecision {
  const unresolved = blanks.filter(
    (blank) => blank.required && !blank.resolved,
  );
  const unresolvedRequiredBlankIds = unresolved.map(({ id }) => id);
  const hasUnresolvedGrid = unresolved.some(
    ({ fieldType }) => fieldType === "grid",
  );
  const registrationAccepted = registration.status === "accepted";
  const reasons: AutomaticWorkflowReason[] = [];

  if (registration.status === "unavailable")
    reasons.push("REGISTRATION_UNAVAILABLE");
  else if (!registrationAccepted) reasons.push("REGISTRATION_REJECTED");
  if (unresolvedRequiredBlankIds.length > 0)
    reasons.push("UNRESOLVED_REQUIRED_FIELDS");

  return {
    manualGridRegistrationOffered: !registrationAccepted || hasUnresolvedGrid,
    mode:
      registrationAccepted && unresolvedRequiredBlankIds.length === 0
        ? "automatic"
        : "manual-recovery",
    reasons,
    unresolvedRequiredBlankIds,
  };
}

/** Plain-language status for the pre-flight workflow band. */
export function describeAutomaticWorkflow(
  decision: AutomaticWorkflowDecision,
): string {
  if (decision.mode === "automatic")
    return "Automatic: the trace registered against the local basemap and every required value is resolved from source evidence.";

  const details: string[] = [];
  if (decision.reasons.includes("REGISTRATION_UNAVAILABLE"))
    details.push("automatic trace registration is not available in this build");
  if (decision.reasons.includes("REGISTRATION_REJECTED"))
    details.push("automatic trace registration was not confident enough");
  if (decision.unresolvedRequiredBlankIds.length > 0)
    details.push(
      `${String(decision.unresolvedRequiredBlankIds.length)} required value${decision.unresolvedRequiredBlankIds.length === 1 ? "" : "s"} could not be resolved from source evidence`,
    );

  return `Manual recovery: ${details.join("; ")}.`;
}
